import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import SMSDialog from '../../components/dialogs/SMSDialog';
import AddReservationDialog from '../../components/dialogs/AddReservationDialog';
import ReservationDetailsDialog from '../../components/dialogs/ReservationDetailsDialog';
import { Toggle } from "@/components/ui/toggle"
import { 
  Calendar, 
  Clock, 
  Users, 
  Phone, 
  CheckCircle, 
  XCircle, 
  Search,
  Plus,
  Ban,
  Mail,
  Loader2
} from 'lucide-react';
import { Input } from '../../components/ui/input';
import CalendarDateDialog from '../../components/dialogs/CalendarDateDialog';
import toast from 'react-hot-toast';
import reservationApi from '../../services/reservationApi';
import webSocketService, { type PendingBookingsResponse } from '../../services/webSocketService';
import type { 
  ReserveTable,
  RestaurantTable,
  RejectRequestDto,
  BookingStatus as BackendBookingStatus
} from '../../types/reservations';

// Convert backend data to frontend Reservation type
interface Reservation {
  id: string;
  bookedId: string;
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  date: string;
  time: string;
  guestNo: number;
  specialRequests?: string;
  status: 'pending' | 'accepted' | 'cancelled' | 'completed' | 'incompleted' | 'rejected';
  createdAt: string;
  tableId?: string;
  tableNo: number;
  restaurantTable?: RestaurantTable;
  restaurantTableId?: Array<{ id: number; name: string }>;
  mergeGroupName?: string; // Merge group name for merged table reservations
  mergeGroupColor?: string; // Merge group color for merged table reservations
}

const convertReserveTableToReservation = (reserveTable: ReserveTable): Reservation => {
  const statusMap: Record<BackendBookingStatus, Reservation['status']> = {
    'Pending': 'pending',
    'Accepted': 'accepted',
    'Rejected': 'rejected',
    'Completed': 'completed',
    'Inomplete': 'incompleted',
    'Canceled': 'cancelled'
  };

  return {
    id: reserveTable.id.toString(),
    bookedId: reserveTable.bookedId,
    customerName: reserveTable.username,
    customerEmail: reserveTable.email,
    customerPhone: reserveTable.phoneNo,
    date: reserveTable.date,
    time: reserveTable.time,
    guestNo: reserveTable.guestNo,
    specialRequests: reserveTable.message,
    status: statusMap[reserveTable.status] || 'pending',
    createdAt: new Date().toISOString(),
    tableId: reserveTable.tableNo?.toString(),
    tableNo: reserveTable.tableNo || 0,
    restaurantTable: reserveTable.restaurantTable,
    restaurantTableId: reserveTable.restaurantTableId, // Include the array of table IDs
    mergeGroupName: reserveTable.mergeGroupName, // Include merge group name
    mergeGroupColor: reserveTable.mergeGroupColor // Include merge group color
  };
};

const convertReservationStatusToBackendStatus = (status: string): BackendBookingStatus => {
  switch (status.toLowerCase()) {
    case 'accepted':
      return 'Accepted';
    case 'cancelled':
      return 'Rejected';
    case 'rejected':
      return 'Rejected';
    case 'completed':
      return 'Completed';
    case 'incompleted':
      return 'Inomplete'; // Backend has typo
    default:
      return 'Pending';
  }
};

// Simple Calendar View Component
function CalendarView({ 
  reservations, 
  onDateClick, 
  onReservationClick 
}: { 
  readonly reservations: Reservation[];
  readonly onDateClick: (date: string) => void;
  readonly onReservationClick: (reservation: Reservation) => void;
}) {
  const today = new Date();
  const [currentMonth, setCurrentMonth] = useState(today.getMonth());
  const [currentYear, setCurrentYear] = useState(today.getFullYear());
  
  // Get first day of month and number of days
  const firstDayOfMonth = new Date(currentYear, currentMonth, 1);
  const lastDayOfMonth = new Date(currentYear, currentMonth + 1, 0);
  const daysInMonth = lastDayOfMonth.getDate();
  const startingDayOfWeek = firstDayOfMonth.getDay();
  
  // Group reservations by date - only include reservations from current month/year
  const reservationsByDate = reservations.reduce((acc, reservation) => {
    const reservationDate = new Date(reservation.date);
    // Only include reservations from the current calendar month/year
    if (reservationDate.getMonth() === currentMonth && reservationDate.getFullYear() === currentYear) {
      const day = reservationDate.getDate();
      if (!acc[day]) acc[day] = [];
      acc[day].push(reservation);
    }
    return acc;
  }, {} as Record<number, Reservation[]>);
  
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  
  const getReservationStatusClass = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'accepted': return 'bg-green-100 text-green-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };
  
  const goToPreviousMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  const goToNextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };

  return (
    <div className="bg-white rounded-lg border">
      <div className="p-4 border-b flex items-center justify-between">
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={goToPreviousMonth}
          className="hover:bg-gray-100"
        >
          ← Previous
        </Button>
        <h3 className="text-lg font-semibold">
          {monthNames[currentMonth]} {currentYear}
        </h3>
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={goToNextMonth}
          className="hover:bg-gray-100"
        >
          Next →
        </Button>
      </div>
      <div className="grid grid-cols-7 gap-px bg-gray-200">
        {dayNames.map(day => (
          <div key={day} className="bg-gray-50 p-2 text-center text-sm font-medium text-gray-500">
            {day}
          </div>
        ))}
        {/* Empty cells for days before month starts */}
        {new Array(startingDayOfWeek).fill(null).map((_, index) => (
          <div key={`empty-start-${currentYear}-${currentMonth}-${index}`} className="bg-white p-2 h-24"></div>
        ))}
        {/* Days of the month */}
        {new Array(daysInMonth).fill(null).map((_, index) => {
          const day = index + 1;
          const dayReservations = reservationsByDate[day] || [];
          const isToday = day === today.getDate();
          
          return (
                        <button 
              key={day} 
              type="button"
              className={`bg-white p-2 h-24 border-r border-b cursor-pointer hover:bg-gray-50 transition-colors text-left w-full ${isToday ? 'bg-blue-50 hover:bg-blue-100' : ''}`}
              onClick={() => {
                // Construct date string directly to avoid timezone issues
                const paddedMonth = String(currentMonth + 1).padStart(2, '0'); // months are 0-indexed
                const paddedDay = String(day).padStart(2, '0');
                const dateStr = `${currentYear}-${paddedMonth}-${paddedDay}`;
                onDateClick(dateStr);
              }}
            >
              <div className={`text-sm font-medium ${isToday ? 'text-blue-600' : 'text-gray-900'}`}>
                {day}
              </div>
              <div className="mt-1 space-y-1">
                {dayReservations.slice(0, 2).map((reservation) => (
                  <button 
                    key={`cal-${reservation.id}-${reservation.bookedId}`} 
                    type="button"
                    className={`text-xs px-2 py-1 rounded truncate cursor-pointer hover:opacity-80 text-left w-full ${getReservationStatusClass(reservation.status)}`}
                    onClick={(e) => {
                      e.stopPropagation(); // Prevent date click
                      onReservationClick(reservation);
                    }}
                  >
                    {reservation.time} - {reservation.customerName}
                  </button>
                ))}
                {dayReservations.length > 2 && (
                  <div className="text-xs text-gray-500 cursor-pointer hover:text-gray-700">
                    +{dayReservations.length - 2} more
                  </div>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

const ReservationsList = React.memo(function ReservationsList() {
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('pending');
  const [dateFilter, setDateFilter] = useState<string>('');
  const [viewMode, setViewMode] = useState<'list' | 'calendar'>('list');
  const [smsDialogOpen, setSmsDialogOpen] = useState(false);
  const [selectedReservation, setSelectedReservation] = useState<Reservation | null>(null);
  const [smsAction, setSmsAction] = useState<'accept' | 'reject'>('accept');
  const [loading, setLoading] = useState(true);
  const [addReservationOpen, setAddReservationOpen] = useState(false);
  const [reservationDetailsOpen, setReservationDetailsOpen] = useState(false);
  const [selectedReservationForDetails, setSelectedReservationForDetails] = useState<Reservation | null>(null);
  const [calendarDateDialogOpen, setCalendarDateDialogOpen] = useState(false);
  const [selectedDateReservations, setSelectedDateReservations] = useState<Reservation[]>([]);
  const [selectedDate, setSelectedDate] = useState<string>('');
  
  // Database-driven status counts with optimistic updates
  const [statusCounts, setStatusCounts] = useState({
    pending: 0,
    accepted: 0,
    cancelled: 0,
    rejected: 0,
    total: 0
  });
  
  // Loading and error states for better UX
  const [countsLoading, setCountsLoading] = useState(true);
  
  // Request deduplication state
  const fetchingReservations = useRef(false);
  const fetchingCounts = useRef(false);
  
  // Track optimistic updates to prevent count flickering
  const [optimisticUpdates, setOptimisticUpdates] = useState<Record<string, { from: string; to: string }>>({});
  
  // Track cancellation loading state
  const [cancellingId, setCancellingId] = useState<string | null>(null);


  // Utility function to get status color
  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'accepted': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      case 'complete': return 'bg-blue-100 text-blue-800';
      case 'cancelled': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Combined function to fetch both reservations and counts in one API call
  const fetchReservationsAndCounts = useCallback(async (showRefresh = false) => {
    // Prevent duplicate calls
    if (fetchingReservations.current || fetchingCounts.current) {
      return;
    }

    try {
      fetchingReservations.current = true;
      fetchingCounts.current = true;
      setLoading(!showRefresh);
      setCountsLoading(true);
      
      // Single API call gets all data needed
      const standardizedData = await reservationApi.getStandardizedReservations();
      
      // Calculate counts from relevant reservations (pending, accepted, cancelled, rejected)
      const relevantReservations = standardizedData.all?.filter(r => 
        ['Pending', 'Accepted', 'Canceled', 'Rejected'].includes(r.status)
      ) || [];
      
      const counts = {
        pending: relevantReservations.filter((r: ReserveTable) => r.status === 'Pending').length,
        accepted: relevantReservations.filter((r: ReserveTable) => r.status === 'Accepted').length,
        cancelled: relevantReservations.filter((r: ReserveTable) => r.status === 'Canceled').length,
        rejected: relevantReservations.filter((r: ReserveTable) => r.status === 'Rejected').length,
        total: relevantReservations.length
      };
      
      setStatusCounts(counts);
      setCountsLoading(false);
      
      // Clear any optimistic updates since we have fresh data
      setOptimisticUpdates({});

      const convertedReservations = standardizedData.all
        .filter(r => ['Pending', 'Accepted', 'Canceled', 'Rejected'].includes(r.status))
        .map(convertReserveTableToReservation);
      
      // Sort to show pending first, then by date and time (newest first)
      convertedReservations.sort((a, b) => {
        // Pending reservations always come first
        if (a.status === 'pending' && b.status !== 'pending') return -1;
        if (b.status === 'pending' && a.status !== 'pending') return 1;
        
        // For pending reservations, sort by FIFO (first received first) using ID
        if (a.status === 'pending' && b.status === 'pending') {
          return Number.parseInt(a.id, 10) - Number.parseInt(b.id, 10); // Lower ID = received earlier
        }
        
        // For non-pending reservations, sort by date and time (newest first)
        const dateCompare = new Date(b.date).getTime() - new Date(a.date).getTime();
        if (dateCompare !== 0) return dateCompare;
        return b.time.localeCompare(a.time);
      });

      setReservations(convertedReservations);

     


    } catch (error) {
      console.error('Failed to fetch reservations:', error);
      toast.error('Failed to fetch reservations. Please check your connection.');
      // Set empty state on error instead of crashing
      setReservations([]);
      setStatusCounts({ pending: 0, accepted: 0, cancelled: 0, rejected: 0, total: 0 });
    } finally {
      setLoading(false);
      setCountsLoading(false);
      fetchingReservations.current = false;
      fetchingCounts.current = false;
    }
  }, []);



  // WebSocket connection and component registration
  useEffect(() => {
    const componentId = 'ReservationsList';
    let statusTimeout: NodeJS.Timeout | null = null;
    let mounted = true;
    
    // Skip if already registered (React StrictMode prevention)
    if (!mounted) return;
    
    // Get initial connection status before subscribing
    const initialStatus = webSocketService.getConnectionStatus() === 'connected';
    
    // Register this component with the WebSocket service
    webSocketService.registerComponent(componentId);
    
    // Listen to connection status with debouncing to prevent flicker
    const connectionSub = webSocketService.connectionStatus().subscribe({
      next: (connected: boolean) => {
        if (!mounted) return; // Skip if component unmounted
        
        // Debounce status updates to prevent red flicker during tab switches
        if (statusTimeout) clearTimeout(statusTimeout);
        statusTimeout = setTimeout(() => {
          if (mounted) {
            // Status updated
          }
        }, 100); // 100ms debounce
      },
      error: (error: unknown) => {
        if (!mounted) return; // Skip if component unmounted
      }
    });

    // Listen to reservation updates with debouncing to prevent excessive API calls
    let updateTimeout: NodeJS.Timeout | null = null;
    const updatesSub = webSocketService.pendingBookings().subscribe({
      next: (pendingData: PendingBookingsResponse) => {
        if (!mounted) return; // Skip if component unmounted        
        // Debounce updates to prevent multiple rapid API calls
        if (updateTimeout) clearTimeout(updateTimeout);
        updateTimeout = setTimeout(() => {
          if (mounted) {
            // Use the combined function to reduce API calls
            fetchReservationsAndCounts(true);
          }
        }, 1000); // 1 second debounce for updates
      },
      error: (error: unknown) => {
        if (!mounted) return; // Skip if component unmounted
      }
    });

    // Cleanup function
    return () => {
      mounted = false;
      if (statusTimeout) clearTimeout(statusTimeout);
      if (updateTimeout) clearTimeout(updateTimeout);
      webSocketService.unregisterComponent(componentId);
      connectionSub.unsubscribe();
      updatesSub.unsubscribe();
    };
  }, [fetchReservationsAndCounts]);

  // Fetch reservations and status counts on mount with circuit breaker
  useEffect(() => {
    let abortController: AbortController | null = null;
    let mounted = true;
    let failureCount = 0;
    const MAX_FAILURES = 3;
    const BACKOFF_BASE = 2000;

    const loadWithBackoff = async (retries = 2) => {
      // Skip if component unmounted or too many failures
      if (!mounted || failureCount >= MAX_FAILURES) {
        return;
      }

      abortController = new AbortController();
      
      try {
        // Use the combined function to avoid duplicate API calls
        await fetchReservationsAndCounts();
        failureCount = 0; // Reset on success
      } catch (error) {
        console.error('Error loading reservations with backoff:', error);
        failureCount++;
        
        if (retries > 0 && mounted && failureCount < MAX_FAILURES) {
          const delay = BACKOFF_BASE * Math.pow(2, 2 - retries); // Exponential backoff
          setTimeout(() => {
            if (mounted) loadWithBackoff(retries - 1);
          }, delay);
        } else {
          toast.error('🚫 too many failures');
        }
      }
    };

    loadWithBackoff();

    // Cleanup function
    return () => {
      mounted = false;
      if (abortController) {
        abortController.abort();
      }
    };
  }, [fetchReservationsAndCounts]);
  
  // Reduced periodic sync frequency to minimize server load
  useEffect(() => {
    const interval = setInterval(() => {
      // Only sync if no pending optimistic updates and reduce frequency
      if (Object.keys(optimisticUpdates).length === 0) {
        // Use combined function and reduce frequency to every 2 minutes
        fetchReservationsAndCounts();
      }
    }, 120000); // Sync every 2 minutes instead of 30 seconds (4x reduction)
    
    return () => clearInterval(interval);
  }, [optimisticUpdates, fetchReservationsAndCounts]);

  const handleStatusUpdate = async (reservationId: string, newStatus: 'accepted' | 'cancelled', tableId?: number) => {
    try {
      // Set loading state for cancel button
      if (newStatus === 'cancelled') {
        setCancellingId(reservationId);
      }
      
      // Find the current reservation to get its current status
      const currentReservation = reservations.find(r => r.id === reservationId);
      const oldStatus = currentReservation?.status || 'pending';
      
      // Optimistically update status counts immediately
      setStatusCounts(prev => {
        const updated = { ...prev };
        // Decrease old status count
        if (oldStatus in updated) {
          updated[oldStatus as keyof typeof updated] = Math.max(0, updated[oldStatus as keyof typeof updated] - 1);
        }
        // Increase new status count
        if (newStatus in updated) {
          updated[newStatus as keyof typeof updated] += 1;
        }
        return updated;
      });
      
      // Track this optimistic update
      setOptimisticUpdates(prev => ({
        ...prev,
        [reservationId]: { from: oldStatus, to: newStatus }
      }));
      
      // Extract ALL table IDs from the reservation data (for merged groups)
      const extractTableIds = (reservation: ReserveTable | Reservation | null): number[] => {
        // First try to get ALL IDs from restaurantTableId array (modern structure)
        if (reservation?.restaurantTableId && Array.isArray(reservation.restaurantTableId) && reservation.restaurantTableId.length > 0) {
          return reservation.restaurantTableId.map((table: { id: number; name: string } | number) => 
            typeof table === 'object' ? table.id : table
          );
        }
        // Next try to get ID from restaurantTable object (legacy)
        if (reservation?.restaurantTable?.id) {
          return [reservation.restaurantTable.id];
        }
        // Fallback to tableNo if available and valid (not 0)
        if (reservation?.tableNo && reservation.tableNo > 0) {
          return [reservation.tableNo];
        }
        // Last resort fallback
        toast.error('⚠️ Could not extract table IDs from reservation');
        return [];
      };
      
      // Use provided tableId, or extract all from reservation, or default to [1]
      const restaurantTableIds = tableId ? [tableId] : extractTableIds(currentReservation || null);
      const restaurantTableId = restaurantTableIds[0]; // For backward compatibility with single table operations
      
      // Use dedicated API method for cancellations, generic method for others
      if (newStatus === 'cancelled') {
        await reservationApi.cancelBooking(
          Number.parseInt(reservationId, 10),
          restaurantTableIds,
          `Reservation cancelled by admin`,
          undefined, // customerDetails
          currentReservation?.mergeGroupName,
          currentReservation?.mergeGroupColor
        );
      } else {
        const backendStatus = convertReservationStatusToBackendStatus(newStatus);
        const request: RejectRequestDto = {
          bookingId: Number.parseInt(reservationId, 10),
          bookingStatus: backendStatus,
          restaurantTableId: [restaurantTableId], // Backend expects array
          message: `Status updated to ${newStatus}`
        };
        await reservationApi.manageBooking(request);
      }

      // Update local state immediately
      setReservations(prev => prev.map(res => 
        res.id === reservationId ? { ...res, status: newStatus } : res
      ));

      // Show success message
      toast.success(`Reservation ${newStatus} successfully`);
      
      // Refresh data in background to sync with database (without showing loading)
      setTimeout(() => {
        fetchReservationsAndCounts(false);
      }, 1000);

    } catch (error) {
      console.error(`Failed to ${newStatus} reservation:`, error);
      toast.error(`Failed to ${newStatus} reservation`);
      
      // Revert optimistic update on error
      const currentReservation = reservations.find(r => r.id === reservationId);
      const oldStatus = currentReservation?.status || 'pending';
      
      setStatusCounts(prev => {
        const reverted = { ...prev };
        // Revert the optimistic changes
        if (newStatus in reverted) {
          reverted[newStatus as keyof typeof reverted] = Math.max(0, reverted[newStatus as keyof typeof reverted] - 1);
        }
        if (oldStatus in reverted) {
          reverted[oldStatus as keyof typeof reverted] += 1;
        }
        return reverted;
      });
      
      // Remove the failed optimistic update
      setOptimisticUpdates(prev => {
        const updated = { ...prev };
        delete updated[reservationId];
        return updated;
      });
    } finally {
      // Clear loading state
      setCancellingId(null);
    }
  };

  // Handle SMS sending
  const handleSendSMS = async (
    message: string, 
    selectedTableIds: number[], 
    mergeGroupName?: string, 
    mergeGroupColor?: string
  ) => {
    if (!selectedReservation) return;

    try {
      const oldStatus = selectedReservation.status;
      const newStatus = smsAction === 'accept' ? 'accepted' : 'rejected';
      const actionText = smsAction === 'accept' ? 'accepting' : 'rejecting';
      
      toast.loading(`Processing reservation ${actionText} and sending SMS...`, { id: 'sms-process' });
      
      // Optimistically update status counts immediately
      setStatusCounts(prev => {
        const updated = { ...prev };
        // Decrease old status count
        if (oldStatus in updated) {
          updated[oldStatus as keyof typeof updated] = Math.max(0, updated[oldStatus as keyof typeof updated] - 1);
        }
        // Increase new status count
        if (newStatus in updated) {
          updated[newStatus as keyof typeof updated] += 1;
        }
        return updated;
      });
      
      // Track this optimistic update
      setOptimisticUpdates(prev => ({
        ...prev,
        [selectedReservation.id]: { from: oldStatus, to: newStatus }
      }));
      
      const backendStatus = convertReservationStatusToBackendStatus(newStatus);
      
      // Build request with array of table IDs
      const finalTableIds = selectedTableIds.length > 0 ? selectedTableIds : [selectedReservation.tableNo || 1];
      
      const request: RejectRequestDto = {
        bookingId: parseInt(selectedReservation.id),
        bookingStatus: backendStatus,
        restaurantTableId: finalTableIds,
        message: message || `Status updated to ${newStatus}`,
        // Include merge group metadata directly in request (backend-driven, no localStorage)
        ...(mergeGroupName && selectedTableIds.length >= 2 && {
          mergeGroupName: mergeGroupName.trim(),
          mergeGroupColor: mergeGroupColor
        })
      };
      
      
      try {
        await reservationApi.manageBooking(request);
        
        // Update local state
        setReservations(prev => prev.map(res => 
          res.id === selectedReservation.id ? { 
            ...res, 
            status: newStatus,
            tableId: selectedTableIds.length > 0 ? selectedTableIds[0].toString() : res.tableId
          } : res
        ));

        toast.success(`Reservation ${newStatus} successfully! SMS notification sent to ${selectedReservation.customerName}`, { id: 'sms-process' });
      } catch (apiError: unknown) {
        // Check if error is about table allocation
        const errorMessage = apiError instanceof Error ? apiError.message : String(apiError);
        if (errorMessage.includes('already allocated') || errorMessage.includes('cannot be updated')) {
          // Reservation was likely saved but table status update failed
          // This is acceptable - show success but warn about table conflict
          toast.success(
            `Reservation ${newStatus}! Note: Some tables may already be assigned to other reservations.`, 
            { id: 'sms-process', duration: 5000 }
          );
          
          // Update local state
          setReservations(prev => prev.map(res => 
            res.id === selectedReservation.id ? { 
              ...res, 
              status: newStatus,
              tableId: selectedTableIds.length > 0 ? selectedTableIds[0].toString() : res.tableId
            } : res
          ));
        } else {
          // Different error - throw it to be caught by outer catch
          throw apiError;
        }
      }
      
      // Close dialog
      setSmsDialogOpen(false);
      setSelectedReservation(null);
      
      // Refresh data in background to sync with database (without showing loading)
      setTimeout(() => {
        fetchReservationsAndCounts(false);
      }, 1000);
      
    } catch (error) {
      toast.error(`Failed to ${smsAction} reservation. Please try again.`, { id: 'sms-process' });
      
      // Revert optimistic update on error
      if (selectedReservation) {
        const oldStatus = selectedReservation.status;
        const newStatus = smsAction === 'accept' ? 'accepted' : 'rejected';
        
        setStatusCounts(prev => {
          const reverted = { ...prev };
          // Revert the optimistic changes
          if (newStatus in reverted) {
            reverted[newStatus as keyof typeof reverted] = Math.max(0, reverted[newStatus as keyof typeof reverted] - 1);
          }
          if (oldStatus in reverted) {
            reverted[oldStatus as keyof typeof reverted] += 1;
          }
          return reverted;
        });
        
        // Remove the failed optimistic update
        setOptimisticUpdates(prev => {
          const updated = { ...prev };
          delete updated[selectedReservation.id];
          return updated;
        });
      }
    }
  };

 
  // Enhanced search that uses backend API when needed
  const [searchResults, setSearchResults] = useState<Reservation[]>([]);

  const performBackendSearch = async (searchValue: string) => {
    if (searchValue.length < 3) return; // Only search for 3+ characters
    
    try {
      const results = await reservationApi.searchReservationsWithValue(searchValue);
      const convertedResults = results.map(dto => convertReserveTableToReservation({
        id: parseInt(dto.bookedId.split('_')[0]) || 0, // Extract ID from bookedId
        bookedId: dto.bookedId,
        username: dto.username,
        email: dto.email,
        phoneNo: dto.phoneNo,
        message: dto.message,
        tableNo: 0, // Not available in DTO
        time: dto.time,
        date: dto.date,
        status: dto.status,
        guestNo: dto.guestNo
      } as ReserveTable));
      setSearchResults(convertedResults);
    } catch (error) {
      toast.error('Search failed');
    }
  };

  // Use search results if searching, otherwise filter local data
  const displayReservations = searchTerm.length >= 3 && searchResults.length > 0 
    ? searchResults 
    : reservations;

  const filteredReservations = displayReservations.filter(reservation => {
    if (searchTerm.length >= 3) {
      return true; // Already filtered by backend search
    }
    
    const matchesSearch = reservation.customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         reservation.customerPhone.includes(searchTerm) ||
                         reservation.bookedId.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || reservation.status === statusFilter;
    const matchesDate = !dateFilter || reservation.date === dateFilter;
    return matchesSearch && matchesStatus && matchesDate;
  });

  // Handle search term changes with debouncing
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchTerm.length >= 3) {
        performBackendSearch(searchTerm);
      } else {
        setSearchResults([]);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  // Use database-driven counts for accuracy
  const { pending: pendingCount, accepted: acceptedCount, cancelled: cancelledCount, rejected: rejectedCount } = statusCounts;
  
  // Display loading spinner for counts if still loading
  const displayCount = (count: number) => countsLoading ? '...' : count.toString();
  
  // Calendar click handlers
  const handleDateClick = useCallback((date: string) => {
    const dateReservations = reservations.filter(reservation => {
      return reservation.date === date;
    });
    setSelectedDateReservations(dateReservations);
    setSelectedDate(date);
    setCalendarDateDialogOpen(true);
  }, [reservations]);
  
  const handleReservationClick = useCallback((reservation: Reservation) => {
    setSelectedReservationForDetails(reservation);
    setReservationDetailsOpen(true);
  }, []);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
         
          <p className="text-muted-foreground">
            Manage incoming reservations, confirm bookings, and notify customers
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => setAddReservationOpen(true)}
            variant="outline"
            className="bg-blue-600 text-white hover:bg-blue-500"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Reservation
          </Button>
          
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card 
          className={`cursor-pointer hover:shadow-lg transition-shadow ${statusFilter === 'pending' ? 'ring-2 ring-yellow-500' : ''}`}
          onClick={() => {
            setStatusFilter('pending');
            setDateFilter('');
          }}
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Pending</p>
                <p className="text-2xl font-bold text-yellow-600">{displayCount(pendingCount)}</p>
              </div>
              <Clock className="h-8 w-8 text-yellow-600" />
            </div>
          </CardContent>
        </Card>

        <Card 
          className={`cursor-pointer hover:shadow-lg transition-shadow ${statusFilter === 'accepted' ? 'ring-2 ring-green-500' : ''}`}
          onClick={() => {
            setStatusFilter('accepted');
            setDateFilter('');
          }}
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Accepted</p>
                <p className="text-2xl font-bold text-green-600">{displayCount(acceptedCount)}</p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
          </CardContent>
        </Card>

        <Card 
          className={`cursor-pointer hover:shadow-lg transition-shadow ${statusFilter === 'cancelled' ? 'ring-2 ring-red-500' : ''}`}
          onClick={() => {
            setStatusFilter('cancelled');
            setDateFilter('');
          }}
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Cancelled</p>
                <p className="text-2xl font-bold text-red-600">{displayCount(cancelledCount)}</p>
              </div>
              <Ban className="h-8 w-8 text-red-600" />
            </div>
          </CardContent>
        </Card>

        <Card 
          className={`cursor-pointer hover:shadow-lg transition-shadow ${statusFilter === 'rejected' ? 'ring-2 ring-red-400' : ''}`}
          onClick={() => {
            setStatusFilter('rejected');
            setDateFilter('');
          }}
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Rejected</p>
                <p className="text-2xl font-bold text-red-500">{displayCount(rejectedCount)}</p>
              </div>
              <XCircle className="h-8 w-8 text-red-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-2 md:space-y-0">
            <CardTitle>New Requests</CardTitle>
            <div className="flex items-center space-x-2">
              
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search reservations..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 w-64 bg-background !rounded"
                />
              </div>
              <Toggle
                variant={dateFilter === new Date().toLocaleDateString('en-CA') ? 'default' : 'outline'}
                size="sm"
                pressed={dateFilter === new Date().toLocaleDateString('en-CA')}
                onPressedChange={(pressed) => {
                  const today = new Date().toLocaleDateString('en-CA'); // YYYY-MM-DD format
                  setDateFilter(pressed ? today : '');
                  setStatusFilter('all');
                  toast.success('Showing today\'s reservations');
                }}
                className="whitespace-nowrap"
              >
                <Calendar className="h-4 w-4 mr-1" />
                Today
              </Toggle>

           
              <select 
                className="px-3 py-2 border !rounded text-sm bg-background"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="accepted">Accepted</option>
                <option value="cancelled">Cancelled</option>
                <option value="rejected">Rejected</option>
              </select>
              <Input
                type="date"
                placeholder="Filter by date"
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                className="w-50 bg-background !rounded"
              />
              <div className="flex border rounded-md overflow-hidden">
                <Button
                  variant={viewMode === 'list' ? 'liteblueBg' : 'outline'}
                  size="sm"
                  onClick={() => setViewMode('list')}
                  className="rounded-none border-none"
                >
                  List View
                </Button>
                <Button
                  variant={viewMode === 'calendar' ? 'liteblueBg' : 'outline'}
                  size="sm"
                  onClick={() => setViewMode('calendar')}
                  className="rounded-none border-none"
                >
                  <Calendar className="h-4 w-4 mr-1" />
                  Calendar
                </Button>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {(() => {
              if (loading) {
                return (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                    <p className="text-muted-foreground mt-2">Loading reservations...</p>
                  </div>
                );
              }
              
              if (viewMode === 'calendar') {
                return (
                  <CalendarView 
                    reservations={filteredReservations} 
                    onDateClick={handleDateClick}
                    onReservationClick={handleReservationClick}
                  />
                );
              }
              
              if (filteredReservations.length === 0) {
                return (
                  <div className="text-center py-8 text-muted-foreground">
                    <p>No reservations found.</p>
                  </div>
                );
              }
              
              return (
              filteredReservations.map((reservation) => (
              <div key={`list-${reservation.id}-${reservation.bookedId}`} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between">
                  <div className="space-y-2">
                    <div className="flex items-center space-x-3">
                      <h4 className="font-semibold text-lg">{reservation.customerName}</h4>
                      <Badge className={getStatusColor(reservation.status)}>
                        {reservation.status.charAt(0).toUpperCase() + reservation.status.slice(1)}
                      </Badge>
                      {reservation.status === 'pending' && (
                        <Badge variant="outline" className="text-orange-600 border-orange-600">
                          New
                        </Badge>
                      )}
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-5 gap-4 text-sm text-muted-foreground">
                      <div className="flex items-center space-x-2">
                        <Calendar className="h-4 w-4" />
                        <span>{new Date(reservation.date).toLocaleDateString()}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Clock className="h-4 w-4" />
                        <span>{reservation.time}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Users className="h-4 w-4" />
                        <span>{reservation.guestNo} guests</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Phone className="h-4 w-4" />
                        <span>{reservation.customerPhone}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Mail className="h-4 w-4" />
                        <span>{reservation.customerEmail}</span>
                      </div>
                    </div>

                    {reservation.specialRequests && (
                      <div className="bg-muted p-2 rounded text-sm">
                        <strong>Special Requests:</strong> {reservation.specialRequests}
                      </div>
                    )}
                  </div>

                  <div className="flex items-center space-x-2">
                    {reservation.status === 'pending' && (
                      <>
                        <Button 
                          size="sm"
                          className="border border-green-600 text-green-600 hover:bg-green-200 !rounded"
                          onClick={() => {
                            setSelectedReservation(reservation);
                            setSmsAction('accept');
                            setSmsDialogOpen(true);
                          }}
                        >
                          <CheckCircle className="h-4 w-4 mr-1" />
                          Accept
                        </Button>
                        <Button 
                          className="border border-red-600 text-red-600 hover:bg-red-200 !rounded" 
                          size="sm"
                          onClick={() => {
                            setSelectedReservation(reservation);
                            setSmsAction('reject');
                            setSmsDialogOpen(true);
                          }}
                        >
                          <XCircle className="h-4 w-4 mr-1" />
                          Reject
                        </Button>
                      </>
                    )}

                    {reservation.status === 'accepted' && (
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="text-red-600 hover:text-red-700 border-red-200"
                        disabled={cancellingId === reservation.id}
                        onClick={() => {  
                          handleStatusUpdate(reservation.id, 'cancelled');
                        }}
                      >
                        {cancellingId === reservation.id ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                            Cancelling...
                          </>
                        ) : (
                          <>
                            <XCircle className="h-4 w-4 mr-1" />
                            Cancel Reservation
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
              ))
              );
            })()}
          </div>
        </CardContent>
      </Card>

      {/* SMS Dialog */}
      {selectedReservation && (
        <SMSDialog
          isOpen={smsDialogOpen}
          onClose={() => setSmsDialogOpen(false)}
          onSend={handleSendSMS}
          reservation={{
            id: selectedReservation.id,
            customerName: selectedReservation.customerName,
            phone: selectedReservation.customerPhone,
            email: selectedReservation.customerEmail,
            date: selectedReservation.date,
            time: selectedReservation.time,
            guestNo: selectedReservation.guestNo,
            assignedTable: selectedReservation.tableId
          }}
          action={smsAction}
        />
      )}

      {/* Add Reservation Dialog */}
      <AddReservationDialog
        open={addReservationOpen}
        onClose={() => setAddReservationOpen(false)}
        onSuccess={() => {
          // Don't call fetchReservationsAndCounts here - WebSocket will handle the update
          // to prevent race conditions with the backend notification system
          toast.success('New reservation added successfully!');
        }}
      />
      
      {/* Calendar Date Dialog */}
      <CalendarDateDialog
        open={calendarDateDialogOpen}
        onClose={() => {
          setCalendarDateDialogOpen(false);
          setSelectedDateReservations([]);
          setSelectedDate('');
        }}
        date={selectedDate}
        reservations={selectedDateReservations}
        onReservationClick={handleReservationClick}
      />
      
      {/* Reservation Details Dialog */}
      {selectedReservationForDetails && (
        <ReservationDetailsDialog
          isOpen={reservationDetailsOpen}
          onClose={() => {
            setReservationDetailsOpen(false);
            setSelectedReservationForDetails(null);
          }}
          reservation={{
            id: selectedReservationForDetails.id,
            bookedId: selectedReservationForDetails.bookedId,
            customerName: selectedReservationForDetails.customerName,
            customerEmail: selectedReservationForDetails.customerEmail,
            customerPhone: selectedReservationForDetails.customerPhone,
            date: selectedReservationForDetails.date,
            time: selectedReservationForDetails.time,
            guestNo: selectedReservationForDetails.guestNo,
            specialRequests: selectedReservationForDetails.specialRequests,
            status: selectedReservationForDetails.status,
            tableNo: selectedReservationForDetails.tableNo,
            createdAt: selectedReservationForDetails.createdAt,
            updatedAt: selectedReservationForDetails.createdAt // Use createdAt as fallback
          }}
        />
      )}
    </div>
  );
});

export default ReservationsList;
